import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, List, ListItemButton, ListItemText,
  Radio, RadioGroup, Stack, Switch, TextField, Typography,
} from "@mui/material";
import { Plus, Trash2, ArrowUp, ArrowDown, Pencil } from "lucide-react";
import { GlassCard, T, TYPE, labelSx, primaryButtonSx } from "../../user/complaints/shared";
import { pm, errorMessage } from "../api";
import { usePm } from "../PmContext";
import { MultiSelect, PersonPicker, Select, fieldSx } from "../inputs";
import { ConditionRows } from "../RuleEditors";
import { FilterBuilder } from "../IssueNavigator";
import { AutomationRules } from "../project/ProjectSettings";

const Section = ({ title, subtitle, action, children }) => (
  <GlassCard sx={{ p: 2.5, mb: 2.5 }}>
    <Stack direction="row" alignItems="flex-start" sx={{ mb: 2 }}>
      <Box sx={{ flexGrow: 1 }}><Typography sx={{ fontWeight: 700, fontSize: TYPE.cardTitle }}>{title}</Typography>{subtitle && <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>{subtitle}</Typography>}</Box>
      {action}
    </Stack>
    {children}
  </GlassCard>
);

const CARD_FIELD_LABELS = {
  type: "Issue type", priority: "Priority", assignee: "Assignee", reporter: "Reporter", storyPoints: "Estimate / points", labels: "Labels", components: "Components", sprint: "Sprint", epic: "Epic",
  dueDate: "Due date", estimate: "Original estimate", timeSpent: "Time spent", subtasks: "Sub-task progress", attachments: "Attachment count", comments: "Comment count",
  links: "Linked issues", blocked: "Blocked indicator", flagged: "Flagged indicator", daysInColumn: "Days in column",
};

/** Saves one section; shows server validation messages. */
const useSave = (board, reloadBoard) => {
  const ctx = usePm();
  return async (patch, msg = "Board saved") => {
    try {
      await pm.updateBoard(board._id, patch);
      ctx.notify(msg);
      reloadBoard();
      return true;
    } catch (err) {
      ctx.notify(errorMessage(err, "Not saved"), "error");
      return false;
    }
  };
};

// ---------------------------------------------------------------------------
const General = ({ board, save }) => {
  const [f, setF] = useState(board);
  useEffect(() => setF(board), [board]);
  return (
    <Section title="General" action={<Button variant="contained" sx={primaryButtonSx} onClick={() => save({ name: f.name, key: f.key, description: f.description, type: f.type, isDefault: f.isDefault })}>Save</Button>}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={8}><TextField fullWidth size="small" label="Board name" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} sx={fieldSx} /></Grid>
        <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Key" value={f.key || ""} onChange={(e) => setF({ ...f, key: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10) })} sx={fieldSx} /></Grid>
        <Grid item xs={12}><TextField fullWidth multiline minRows={2} size="small" label="Description" value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} sx={fieldSx} /></Grid>
        <Grid item xs={12} sm={6}>
          <Select label="Board type" value={f.type} onChange={(type) => setF({ ...f, type })} options={[{ value: "scrum", label: "Scrum (sprints)" }, { value: "kanban", label: "Kanban (continuous flow)" }]} helperText={f.type !== board.type ? "Switching keeps every issue and sprint; only the board's behaviour changes." : ""} />
        </Grid>
        <Grid item xs={12} sm={6}><FormControlLabel control={<Switch checked={Boolean(f.isDefault)} onChange={(e) => setF({ ...f, isDefault: e.target.checked })} />} label="Project default board" /></Grid>
      </Grid>
    </Section>
  );
};

const Access = ({ board, save }) => {
  const ctx = usePm();
  const [f, setF] = useState({});
  useEffect(() => setF({ visibility: board.visibility, memberIds: (board.memberIds || []).map(String), teamIds: (board.teamIds || []).map(String), adminIds: (board.adminIds || []).map(String) }), [board]);
  return (
    <Section title="Access" subtitle="Who can open this board. People still only see issues of projects they can access." action={<Button variant="contained" sx={primaryButtonSx} onClick={() => save(f)}>Save</Button>}>
      <Stack spacing={2}>
        <Select label="Visibility" value={f.visibility} onChange={(visibility) => setF({ ...f, visibility })} options={[["project", "Project members (everyone who can view project boards)"], ["users", "Selected people"], ["teams", "Selected teams"], ["private", "Only the owner and board admins"], ["organization", "Organisation-wide"]].map(([value, label]) => ({ value, label }))} />
        {f.visibility === "users" && <PersonPicker label="People" multiple value={f.memberIds} onChange={(memberIds) => setF({ ...f, memberIds })} />}
        {f.visibility === "teams" && <MultiSelect label="Teams" value={f.teamIds || []} onChange={(teamIds) => setF({ ...f, teamIds })} options={ctx.teams.map((t) => ({ value: String(t._id), label: t.name }))} />}
        <PersonPicker label="Board admins (may configure this board)" multiple value={f.adminIds} onChange={(adminIds) => setF({ ...f, adminIds })} />
        <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Owner: {board.owner?.name || "—"}. Project admins with board.manage can always configure boards.</Typography>
      </Stack>
    </Section>
  );
};

const Source = ({ board, save }) => {
  const [filters, setFilters] = useState([]);
  const [kind, setKind] = useState(board.source?.kind || "project");
  const [filterId, setFilterId] = useState(board.source?.filterId || null);
  const [query, setQuery] = useState({ all: [], any: [], none: [], ...(board.source?.query || {}) });
  useEffect(() => {
    pm.filters(board.project._id).then(setFilters).catch(() => {});
  }, [board.project._id]);
  return (
    <Section title="Issue source" subtitle="Which project issues appear on this board. Issues are never copied — every board is a live view." action={<Button variant="contained" sx={primaryButtonSx} onClick={() => save({ source: { kind, filterId: kind === "filter" ? filterId : null, query } })}>Save</Button>}>
      <RadioGroup row value={kind} onChange={(e) => setKind(e.target.value)}>
        <FormControlLabel value="project" control={<Radio size="small" />} label={<Typography sx={{ fontSize: TYPE.small }}>All issues of {board.project.key}</Typography>} />
        <FormControlLabel value="filter" control={<Radio size="small" />} label={<Typography sx={{ fontSize: TYPE.small }}>Saved filter</Typography>} />
      </RadioGroup>
      {kind === "filter" && <Box sx={{ my: 1.5 }}><Select label="Saved filter" value={filterId} onChange={setFilterId} options={filters.map((f) => ({ value: f._id, label: f.name }))} /></Box>}
      <Typography sx={{ ...labelSx, mt: 1.5, mb: 1 }}>Extra conditions (optional, AND / OR / NOT)</Typography>
      <FilterBuilder query={query} onChange={setQuery} sprints={[]} />
    </Section>
  );
};

const COLUMN_COLORS = [
  { label: "Default / Gray", value: "" },
  { label: "Blue", value: "#0052CC" },
  { label: "Green", value: "#00875A" },
  { label: "Amber", value: "#FFAB00" },
  { label: "Red", value: "#DE350B" },
  { label: "Purple", value: "#6554C0" },
  { label: "Cyan", value: "#00B8D9" },
  { label: "Slate", value: "#42526E" },
  { label: "Emerald", value: "#36B37E" },
  { label: "Orange", value: "#FF5630" },
  { label: "Violet", value: "#8777D9" },
];

const ColumnDialog = ({ state, board, used, onClose, onSave }) => {
  const ctx = usePm();
  const [c, setC] = useState(null);
  useEffect(() => {
    if (state) {
      setC({
        name: "",
        color: "",
        wipLimit: 0,
        minLimit: 0,
        agingDays: 0,
        ...state,
        color: state.color || "",
        policy: { entry: "", exit: "", entryRules: { all: [], any: [] }, exitRules: { all: [], any: [] }, enforce: false, ...(state.policy || {}) },
        statusIds: (state.statusIds || []).map(String),
      });
    }
  }, [state]);
  if (!state || !c) return null;
  const options = (board.workflowStatusIds || [])
    .map(String)
    .filter((s) => !used.has(s) || c.statusIds.includes(s))
    .map((s) => {
      const statusObj = ctx.statusById.get(s);
      const colMatch = board.columns?.find((col) => (col.statusIds || []).map(String).includes(s));
      const label = statusObj?.name || (c.statusIds.includes(s) && c.name) || colMatch?.name || `Status (${s.slice(-6)})`;
      return { value: s, label };
    });

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{state._id ? `Column “${state.name}”` : "New column"}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} sx={fieldSx} /></Grid>
          <Grid item xs={4} sm={2}><TextField fullWidth size="small" type="number" label="WIP max" value={c.wipLimit} onChange={(e) => setC({ ...c, wipLimit: Number(e.target.value) })} sx={fieldSx} /></Grid>
          <Grid item xs={4} sm={2}><TextField fullWidth size="small" type="number" label="Min" value={c.minLimit} onChange={(e) => setC({ ...c, minLimit: Number(e.target.value) })} sx={fieldSx} /></Grid>
          <Grid item xs={4} sm={2}><TextField fullWidth size="small" type="number" label="Aging days" helperText="0 = board default" value={c.agingDays} onChange={(e) => setC({ ...c, agingDays: Number(e.target.value) })} sx={fieldSx} /></Grid>

          {/* Column Color / Header Accent */}
          <Grid item xs={12}>
            <Typography sx={{ ...labelSx, mb: 1 }}>Column Color / Header Accent</Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {COLUMN_COLORS.map((col) => {
                const isSelected = (!col.value && !c.color) || c.color === col.value;
                return (
                  <Box
                    key={col.label}
                    onClick={() => setC({ ...c, color: col.value })}
                    sx={{
                      cursor: "pointer",
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: col.value || "#E2E8F0",
                      border: isSelected ? "3px solid #0052CC" : "2px solid #DFE1E6",
                      boxShadow: isSelected ? "0 0 0 2px rgba(0,82,204,0.3)" : "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "transform .1s",
                      "&:hover": { transform: "scale(1.15)" },
                    }}
                    title={col.label}
                  />
                );
              })}
              <TextField
                size="small"
                placeholder="#0052CC"
                label="Custom Hex"
                value={c.color || ""}
                onChange={(e) => setC({ ...c, color: e.target.value })}
                sx={{ ...fieldSx, width: 130, ml: 1 }}
              />
            </Stack>
          </Grid>

          <Grid item xs={12}><MultiSelect label="Workflow statuses in this column" value={c.statusIds} onChange={(statusIds) => setC({ ...c, statusIds })} options={options} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" multiline minRows={2} label="Entry criteria (shown on the column)" value={c.policy.entry} onChange={(e) => setC({ ...c, policy: { ...c.policy, entry: e.target.value } })} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" multiline minRows={2} label="Exit criteria" value={c.policy.exit} onChange={(e) => setC({ ...c, policy: { ...c.policy, exit: e.target.value } })} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={6}><Typography sx={{ ...labelSx, mb: 0.5 }}>Entry rules (checked when a card enters)</Typography><ConditionRows value={c.policy.entryRules} onChange={(entryRules) => setC({ ...c, policy: { ...c.policy, entryRules } })} /></Grid>
          <Grid item xs={12} sm={6}><Typography sx={{ ...labelSx, mb: 0.5 }}>Exit rules (checked when a card leaves)</Typography><ConditionRows value={c.policy.exitRules} onChange={(exitRules) => setC({ ...c, policy: { ...c.policy, exitRules } })} /></Grid>
          <Grid item xs={12}><FormControlLabel control={<Switch checked={c.policy.enforce} onChange={(e) => setC({ ...c, policy: { ...c.policy, enforce: e.target.checked } })} />} label={<Typography sx={{ fontSize: TYPE.small }}>Enforce rules (otherwise moves show a warning only)</Typography>} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" sx={primaryButtonSx} disabled={!c.name} onClick={() => onSave(c)}>Save column</Button></DialogActions>
    </Dialog>
  );
};

const Columns = ({ board, reloadBoard }) => {
  const ctx = usePm();
  const [edit, setEdit] = useState(null);

  useEffect(() => {
    if (ctx.reload) ctx.reload();
  }, [ctx]);

  const run = async (fn, msg) => {
    try {
      await fn();
      if (msg) ctx.notify(msg);
      reloadBoard();
      if (ctx.reload) ctx.reload();
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  };
  const used = new Set(board.columns.flatMap((c) => c.statusIds.map(String)));
  const unmapped = board.workflowStatusIds.map(String).filter((s) => !used.has(s));
  const ids = board.columns.map((c) => c._id);
  const reorder = (i, d) => {
    const next = [...ids];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    run(() => pm.reorderColumns(board._id, next));
  };
  return (
    <Section title="Columns & statuses" subtitle="Each column shows one or more workflow statuses. Moving a card runs the workflow transition to that status."
      action={<Button size="small" variant="contained" sx={primaryButtonSx} startIcon={<Plus size={13} />} onClick={() => setEdit({})}>Add column</Button>}>
      {board.columns.map((c, i) => (
        <Stack key={c._id} direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ py: 1, borderBottom: `1px solid ${T.border}` }}>
          <Box sx={{ minWidth: 170 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              {c.color && <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: c.color, flexShrink: 0 }} />}
              <Typography sx={{ fontWeight: 700, fontSize: TYPE.small }}>{c.name}{board.type === "kanban" && board.backlogEnabled && i === 0 ? " (backlog)" : ""}</Typography>
            </Stack>
            <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{c.wipLimit ? `WIP ${c.minLimit ? `${c.minLimit}–` : ""}${c.wipLimit}` : "No WIP limit"}{c.policy?.enforce ? " · enforced policy" : c.policy?.entry || c.policy?.exit ? " · policy" : ""}</Typography>
          </Box>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ flexGrow: 1 }}>
            {c.statusIds.map((s) => {
              const sObj = ctx.statusById.get(String(s));
              const sName = sObj?.name || c.name || String(s).slice(-6);
              return <Chip key={s} size="small" label={sName} sx={{ height: 22, bgcolor: sObj?.color ? `${sObj.color}22` : (c.color ? `${c.color}22` : "#E2E8F0") }} />;
            })}
            {!c.statusIds.length && <Typography sx={{ fontSize: TYPE.micro, color: "#B54708" }}>No status mapped — nothing can land here</Typography>}
          </Stack>
          <Stack direction="row">
            <IconButton size="small" disabled={i === 0} onClick={() => reorder(i, -1)}><ArrowUp size={14} /></IconButton>
            <IconButton size="small" disabled={i === ids.length - 1} onClick={() => reorder(i, 1)}><ArrowDown size={14} /></IconButton>
            <IconButton size="small" onClick={() => setEdit(c)}><Pencil size={14} /></IconButton>
            <IconButton size="small" disabled={board.columns.length <= 1} onClick={() => window.confirm(`Delete column “${c.name}”? Its statuses become unmapped (issues are kept).`) && run(() => pm.deleteColumn(board._id, c._id), "Column deleted")}><Trash2 size={14} /></IconButton>
          </Stack>
        </Stack>
      ))}
      {unmapped.length > 0 && <Alert severity="warning" sx={{ mt: 1.5 }}>Unmapped statuses (issues in them are hidden): {unmapped.map((s) => ctx.statusById.get(s)?.name).join(", ")}</Alert>}
      <ColumnDialog state={edit} board={board} used={used} onClose={() => setEdit(null)}
        onSave={(c) => { const { _id, ...body } = c; setEdit(null); run(() => (_id ? pm.updateColumn(board._id, _id, body) : pm.addColumn(board._id, body)), _id ? "Column saved" : "Column added"); }} />
    </Section>
  );
};

const Wip = ({ board, save }) => {
  const [mode, setMode] = useState(board.wip?.mode || "warn");
  const [countSubtasks, setCount] = useState(board.wip?.countSubtasks !== false);
  return (
    <Section title="WIP limits" subtitle="Limits are set per column (Columns). Choose what happens when a move would exceed one." action={<Button variant="contained" sx={primaryButtonSx} onClick={() => save({ wip: { mode, countSubtasks } })}>Save</Button>}>
      <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
        {[["warn", "Warn only — the move happens, people are notified"], ["confirm", "Ask for confirmation"], ["block", "Prevent the move"], ["none", "No restriction (limits are informational)"]].map(([v, l]) => (
          <FormControlLabel key={v} value={v} control={<Radio size="small" />} label={<Typography sx={{ fontSize: TYPE.small }}>{l}</Typography>} />
        ))}
      </RadioGroup>
      <FormControlLabel control={<Checkbox size="small" checked={countSubtasks} onChange={(e) => setCount(e.target.checked)} />} label={<Typography sx={{ fontSize: TYPE.small }}>Count sub-tasks toward WIP</Typography>} />
    </Section>
  );
};

const Swimlanes = ({ board, save }) => {
  const [mode, setMode] = useState(board.swimlane || "none");
  const [lanes, setLanes] = useState(board.swimlaneQueries || []);
  const [open, setOpen] = useState(null);
  return (
    <Section title="Swimlanes" subtitle="Default grouping for this board (each person can switch it on the board)." action={<Button variant="contained" sx={primaryButtonSx} onClick={() => save({ swimlane: mode, swimlaneQueries: lanes })}>Save</Button>}>
      <Select label="Default swimlanes" value={mode} onChange={setMode} options={[["none", "None"], ["assignee", "Assignee"], ["priority", "Priority"], ["epic", "Epic"], ["type", "Issue type"], ["story", "Story (sub-tasks under parents)"], ["parent", "Parent"], ["query", "Queries (custom lanes)"]].map(([value, label]) => ({ value, label }))} />
      {mode === "query" && (
        <Box sx={{ mt: 2 }}>
          {lanes.map((l, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <TextField size="small" label="Lane name" value={l.name} onChange={(e) => setLanes(lanes.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} sx={fieldSx} />
              <Button size="small" onClick={() => setOpen(i)}>Conditions ({(l.query?.all?.length || 0) + (l.query?.any?.length || 0) + (l.query?.none?.length || 0)})</Button>
              <IconButton size="small" onClick={() => setLanes(lanes.filter((_, j) => j !== i))}><Trash2 size={14} /></IconButton>
            </Stack>
          ))}
          <Button size="small" startIcon={<Plus size={13} />} onClick={() => setLanes([...lanes, { name: "Expedite", query: { all: [{ field: "priorityLevel", op: "gte", value: 5 }] } }])}>Add lane</Button>
          <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mt: 1 }}>A card goes to the first lane it matches; the rest go to “Everything else”.</Typography>
        </Box>
      )}
      <Dialog open={open !== null} onClose={() => setOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Lane conditions</DialogTitle>
        <DialogContent dividers>{open !== null && <FilterBuilder query={{ all: [], any: [], none: [], ...lanes[open].query }} onChange={(q) => setLanes(lanes.map((x, j) => (j === open ? { ...x, query: q } : x)))} sprints={[]} />}</DialogContent>
        <DialogActions><Button onClick={() => setOpen(null)}>Done</Button></DialogActions>
      </Dialog>
    </Section>
  );
};

const QuickFilters = ({ board, save }) => {
  const [rows, setRows] = useState(board.quickFilters || []);
  const [open, setOpen] = useState(null);
  useEffect(() => setRows(board.quickFilters || []), [board]);
  return (
    <Section title="Quick filters" subtitle="One-click filters on the board. They use the normal issue search engine (AND / OR / NOT)." action={<Button variant="contained" sx={primaryButtonSx} onClick={() => save({ quickFilters: rows })}>Save</Button>}>
      {rows.map((q, i) => (
        <Stack key={q._id || i} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <TextField size="small" label="Name" value={q.name} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} sx={{ ...fieldSx, width: 220 }} />
          <Button size="small" onClick={() => setOpen(i)}>Conditions ({(q.query?.all?.length || 0) + (q.query?.any?.length || 0) + (q.query?.none?.length || 0)})</Button>
          <IconButton size="small" disabled={i === 0} onClick={() => { const n = [...rows]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; setRows(n); }}><ArrowUp size={14} /></IconButton>
          <IconButton size="small" onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 size={14} /></IconButton>
        </Stack>
      ))}
      <Button size="small" startIcon={<Plus size={13} />} onClick={() => { setRows([...rows, { name: "New filter", query: { all: [], any: [], none: [] } }]); setOpen(rows.length); }}>Add quick filter</Button>
      <Dialog open={open !== null && Boolean(rows[open])} onClose={() => setOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{rows[open]?.name}</DialogTitle>
        <DialogContent dividers>{open !== null && rows[open] && <FilterBuilder query={{ all: [], any: [], none: [], ...rows[open].query }} onChange={(qq) => setRows(rows.map((x, j) => (j === open ? { ...x, query: qq } : x)))} sprints={[]} />}</DialogContent>
        <DialogActions><Button onClick={() => setOpen(null)}>Done</Button></DialogActions>
      </Dialog>
    </Section>
  );
};

const Cards = ({ board, save }) => {
  const ctx = usePm();
  const [fields, setFields] = useState(board.cardFields || []);
  const [colorBy, setColorBy] = useState(board.cardLayout?.colorBy || "none");
  const [cf, setCf] = useState(board.cardLayout?.customFieldKeys || []);
  return (
    <Section title="Card layout" subtitle="What every card on this board shows. Keep it short — boards stay readable." action={<Button variant="contained" sx={primaryButtonSx} onClick={() => save({ cardFields: fields, cardLayout: { colorBy, customFieldKeys: cf } })}>Save</Button>}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, mb: 2 }}>
        {(board.cardFieldOptions || Object.keys(CARD_FIELD_LABELS)).map((k) => (
          <FormControlLabel key={k} control={<Checkbox size="small" checked={fields.includes(k)} onChange={(e) => setFields(e.target.checked ? [...fields, k] : fields.filter((x) => x !== k))} />} label={<Typography sx={{ fontSize: TYPE.small }}>{CARD_FIELD_LABELS[k] || k}</Typography>} />
        ))}
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}><Select label="Card colour stripe" value={colorBy} onChange={setColorBy} options={[["none", "None"], ["priority", "Priority"], ["type", "Issue type"], ["assignee", "Assignee"], ["label", "First label"], ["due", "Due date (overdue / soon)"]].map(([value, label]) => ({ value, label }))} /></Grid>
        <Grid item xs={12} sm={6}><MultiSelect label="Custom fields on cards (up to 5)" value={cf} onChange={(v) => setCf(v.slice(0, 5))} options={ctx.customFields.map((f) => ({ value: f.key, label: f.label }))} /></Grid>
      </Grid>
    </Section>
  );
};

const Estimation = ({ board, save }) => {
  const ctx = usePm();
  const [e, setE] = useState({ unit: "storyPoints", hoursPerDay: 8, customFieldKey: "", ...(board.estimationConfig || {}) });
  const [ranking, setRanking] = useState(board.ranking?.enabled !== false);
  const [backlog, setBacklog] = useState(Boolean(board.backlogEnabled));
  const [hideDone, setHideDone] = useState(board.hideDoneAfterDays ?? 14);
  return (
    <Section title="Estimation, ranking & backlog" action={<Button variant="contained" sx={primaryButtonSx} onClick={() => save({ estimationConfig: e, ranking: { enabled: ranking }, ...(board.type === "kanban" ? { backlogEnabled: backlog, hideDoneAfterDays: hideDone } : {}) })}>Save</Button>}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}><Select label="Estimate with" value={e.unit} onChange={(unit) => setE({ ...e, unit })} options={[["storyPoints", "Story points"], ["originalEstimate", "Original estimate (hours)"], ["remainingEstimate", "Remaining estimate (hours)"], ["days", "Days (original estimate ÷ hours per day)"], ["count", "Issue count"], ["customField", "Custom number field"]].map(([value, label]) => ({ value, label }))} /></Grid>
        <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="number" label="Hours per day" value={e.hoursPerDay} onChange={(ev) => setE({ ...e, hoursPerDay: Number(ev.target.value) })} sx={fieldSx} /></Grid>
        {e.unit === "customField" && <Grid item xs={6} sm={4}><Select label="Custom field" value={e.customFieldKey} onChange={(customFieldKey) => setE({ ...e, customFieldKey })} options={ctx.customFields.filter((f) => ["number", "decimal"].includes(f.type)).map((f) => ({ value: f.key, label: f.label }))} /></Grid>}
        <Grid item xs={12}><FormControlLabel control={<Switch checked={ranking} onChange={(ev) => setRanking(ev.target.checked)} />} label={<Typography sx={{ fontSize: TYPE.small }}>Manual ranking (drag to prioritise; saved for everyone)</Typography>} /></Grid>
        {board.type === "kanban" && (
          <>
            <Grid item xs={12} sm={6}><FormControlLabel control={<Switch checked={backlog} onChange={(ev) => setBacklog(ev.target.checked)} />} label={<Typography sx={{ fontSize: TYPE.small }}>Kanban backlog — the first column ({board.columns[0]?.name}) is planned in the Backlog view</Typography>} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" label="Hide done issues after (days, 0 = never)" value={hideDone} onChange={(ev) => setHideDone(Number(ev.target.value))} sx={fieldSx} /></Grid>
          </>
        )}
      </Grid>
      {board.type === "kanban" && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mt: 1 }}>Kanban never depends on estimates; they only feed card badges and column totals.</Typography>}
    </Section>
  );
};

const Sprints = ({ board, save }) => {
  const [s, setS] = useState(board.settings?.sprintStart || {});
  const [moveTo, setMoveTo] = useState(board.settings?.sprintComplete?.defaultMoveTo || "backlog");
  return (
    <Section title="Sprints" subtitle="Rules checked when a sprint starts, and the default for unfinished work." action={<Button variant="contained" sx={primaryButtonSx} onClick={() => save({ settings: { sprintStart: s, sprintComplete: { defaultMoveTo: moveTo } } })}>Save</Button>}>
      {[["requireIssues", "Sprint must contain at least one issue"], ["requireDates", "Start and end dates are required"], ["requireGoal", "A sprint goal is required"], ["enforceCapacity", "Planned hours must fit the team capacity"]].map(([k, l]) => (
        <FormControlLabel key={k} sx={{ display: "flex" }} control={<Checkbox size="small" checked={Boolean(s[k])} onChange={(e) => setS({ ...s, [k]: e.target.checked })} />} label={<Typography sx={{ fontSize: TYPE.small }}>{l}</Typography>} />
      ))}
      <Box sx={{ mt: 2, maxWidth: 360 }}><Select label="Unfinished issues on completion (default)" value={moveTo} onChange={setMoveTo} options={[["backlog", "Backlog"], ["next", "Next sprint"], ["new", "A new sprint"]].map(([value, label]) => ({ value, label }))} /></Box>
    </Section>
  );
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const Calendar = ({ board, save }) => {
  const st = board.settings || {};
  const [days, setDays] = useState(st.workingDays || [1, 2, 3, 4, 5]);
  const [holidays, setHolidays] = useState(st.holidays || []);
  const [tz, setTz] = useState(st.timezone || "Asia/Kolkata");
  const [aging, setAging] = useState(st.agingDays || 5);
  const [h, setH] = useState("");
  return (
    <Section title="Working days & reports" subtitle="Used by the burndown ideal line, working-day cycle times and aging." action={<Button variant="contained" sx={primaryButtonSx} onClick={() => save({ settings: { workingDays: days, holidays, timezone: tz, agingDays: aging } })}>Save</Button>}>
      <Typography sx={{ ...labelSx, mb: 0.5 }}>Working days</Typography>
      <Stack direction="row" spacing={0.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {DAYS.map((d, i) => <Chip key={d} label={d} color={days.includes(i) ? "primary" : "default"} variant={days.includes(i) ? "filled" : "outlined"} onClick={() => setDays(days.includes(i) ? days.filter((x) => x !== i) : [...days, i].sort())} />)}
      </Stack>
      <Typography sx={{ ...labelSx, mb: 0.5 }}>Holidays</Typography>
      <Stack direction="row" spacing={0.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>{holidays.map((d) => <Chip key={d} label={d} onDelete={() => setHolidays(holidays.filter((x) => x !== d))} />)}</Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField size="small" type="date" value={h} onChange={(e) => setH(e.target.value)} sx={fieldSx} />
        <Button size="small" disabled={!h} onClick={() => { setHolidays([...new Set([...holidays, h])].sort()); setH(""); }}>Add holiday</Button>
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Timezone (IANA)" value={tz} onChange={(e) => setTz(e.target.value)} helperText="e.g. Asia/Kolkata, Europe/London" sx={fieldSx} /></Grid>
        <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" label="Aging threshold (days in status)" value={aging} onChange={(e) => setAging(Number(e.target.value))} sx={fieldSx} /></Grid>
      </Grid>
    </Section>
  );
};

const Notifications = ({ board, save }) => {
  const [wip, setWip] = useState(board.settings?.notifyWip !== false);
  const [cfg, setCfg] = useState(Boolean(board.settings?.notifyConfigChanged));
  return (
    <Section title="Notifications" subtitle="Channels (in-app / email) are chosen per event in Project Settings → Notifications." action={<Button variant="contained" sx={primaryButtonSx} onClick={() => save({ settings: { notifyWip: wip, notifyConfigChanged: cfg } })}>Save</Button>}>
      <FormControlLabel sx={{ display: "flex" }} control={<Switch checked={wip} onChange={(e) => setWip(e.target.checked)} />} label={<Typography sx={{ fontSize: TYPE.small }}>Tell the project lead, manager and board admins when a WIP limit is exceeded</Typography>} />
      <FormControlLabel sx={{ display: "flex" }} control={<Switch checked={cfg} onChange={(e) => setCfg(e.target.checked)} />} label={<Typography sx={{ fontSize: TYPE.small }}>Tell project members when this board's configuration changes</Typography>} />
    </Section>
  );
};

const Danger = ({ board }) => {
  const ctx = usePm();
  const navigate = useNavigate();
  return (
    <Section title="Delete board">
      <Typography sx={{ fontSize: TYPE.small, mb: 1.5 }}>Deleting a board never deletes issues. Its sprints (and their history) move to the project level.</Typography>
      <Button variant="contained" color="error" onClick={async () => { if (!window.confirm(`Delete board “${board.name}”?`)) return; try { await pm.deleteBoard(board._id); ctx.notify("Board deleted"); navigate(`/projects/${board.project.key}/boards`); } catch (err) { ctx.fail(err); } }}>Delete board</Button>
    </Section>
  );
};

const SECTIONS = [
  ["general", "General"], ["access", "Access"], ["source", "Issue source"], ["columns", "Columns & statuses"], ["wip", "WIP limits"], ["swimlanes", "Swimlanes"],
  ["quickFilters", "Quick filters"], ["cards", "Card layout"], ["estimation", "Estimation & ranking"], ["sprints", "Sprints"], ["calendar", "Working days & reports"],
  ["notifications", "Notifications"], ["automation", "Automation"], ["danger", "Delete"],
];

const BoardSettings = ({ board, reloadBoard }) => {
  const [params, setParams] = useSearchParams();
  const save = useSave(board, reloadBoard);
  if (!board.canManage) return <Alert severity="info">You can view this board but not configure it. Ask a board admin or project admin.</Alert>;
  const perms = board.permissions || [];
  const visible = SECTIONS.filter(([k]) => (k !== "sprints" || board.type === "scrum") && (k !== "automation" || perms.includes("automation.manage")));
  const section = visible.some(([k]) => k === params.get("section")) ? params.get("section") : "general";
  const props = { board, save, reloadBoard };
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "220px minmax(0,1fr)" }, gap: 2.5 }}>
      <GlassCard sx={{ p: 1, alignSelf: "start" }}>
        <List dense>{visible.map(([k, l]) => <ListItemButton key={k} selected={section === k} onClick={() => setParams({ section: k }, { replace: true })}><ListItemText primary={l} primaryTypographyProps={{ fontSize: TYPE.small, fontWeight: section === k ? 700 : 500 }} /></ListItemButton>)}</List>
      </GlassCard>
      <Box sx={{ minWidth: 0 }} key={`${board._id}:${board.updatedAt}`}>
        {section === "general" && <General {...props} />}
        {section === "access" && <Access {...props} />}
        {section === "source" && <Source {...props} />}
        {section === "columns" && <Columns {...props} />}
        {section === "wip" && <Wip {...props} />}
        {section === "swimlanes" && <Swimlanes {...props} />}
        {section === "quickFilters" && <QuickFilters {...props} />}
        {section === "cards" && <Cards {...props} />}
        {section === "estimation" && <Estimation {...props} />}
        {section === "sprints" && <Sprints {...props} />}
        {section === "calendar" && <Calendar {...props} />}
        {section === "notifications" && <Notifications {...props} />}
        {section === "automation" && <AutomationRules project={board.project} boardId={board._id} />}
        {section === "danger" && <Danger {...props} />}
      </Box>
    </Box>
  );
};

export default BoardSettings;
